/**
 * Research Service — Firestore data access for the Research tab feature.
 *
 * All reads/writes for Research-related collections funnel through here so:
 *   1. Components stay thin — no Firestore plumbing inside UI files.
 *   2. We can swap data sources later (vendor API via Worker) by changing
 *      one file instead of every consumer.
 *   3. We can mock the entire layer in tests trivially.
 *
 * Firestore collections (see also docs/RESEARCH_SCHEMA.md):
 *
 *   researchConfig/featureFlags        — global feature toggles
 *   researchConfig/sectors             — admin-curated sector list
 *   analystRatings/{ratingId}          — analyst stance per stock
 *   users/{uid}/researchDisclosureAck  — per-user gate acknowledgment
 *   users/{uid}/researchWatchlist      — per-user tracked tickers
 *
 * Module B (long-term investor holdings / 13F) was scoped out — see PRD
 * decisions log.
 */

import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../authentication/firebase-config";

/* ─── Feature flags ──────────────────────────────────────────────── */

const FLAGS_DOC = "researchConfig/featureFlags";

/**
 * Defaults applied when the flags doc does not exist or is unreachable.
 * Everything starts disabled so the feature ships dark until an admin
 * explicitly flips a flag in the Firestore Console.
 */
export const DEFAULT_FLAGS = Object.freeze({
  researchTabEnabled: false,
  moduleAEnabled: true,
  methodPageEnabled: true,
  archetypeViewEnabled: true,
});

export async function fetchFeatureFlags() {
  try {
    const snap = await getDoc(doc(db, FLAGS_DOC));
    if (!snap.exists()) return { ...DEFAULT_FLAGS };
    return { ...DEFAULT_FLAGS, ...snap.data() };
  } catch (err) {
    // Network / permission failure → fail closed (everything off)
    // eslint-disable-next-line no-console
    console.warn("[researchService] feature flags fetch failed:", err.message);
    return { ...DEFAULT_FLAGS };
  }
}

/* ─── Sectors ────────────────────────────────────────────────────── */

/**
 * Sector catalog. Kept hard-coded as a fallback so the UI still works
 * before the admin populates Firestore. Once researchConfig/sectors is
 * seeded with admin curation, that wins.
 */
export const DEFAULT_SECTORS = Object.freeze([
  { id: "tech-ai",     label: "Tech & AI",                  short: "Tech/AI"      },
  { id: "healthcare",  label: "Healthcare & Biotech",       short: "Healthcare"   },
  { id: "metals",      label: "Precious Metals & Miners",   short: "Metals"       },
  { id: "energy",      label: "Energy",                     short: "Energy"       },
]);

export async function fetchSectors() {
  try {
    const snap = await getDoc(doc(db, "researchConfig/sectors"));
    if (!snap.exists() || !Array.isArray(snap.data().items)) {
      return [...DEFAULT_SECTORS];
    }
    return snap.data().items;
  } catch (_) {
    return [...DEFAULT_SECTORS];
  }
}

/* ─── Risk character buckets ─────────────────────────────────────── */

// Three objective buckets we compute from market data. NOT analyst stance,
// NOT a quality judgment. Published methodology lives on the Method page.
export const RISK_BUCKETS = Object.freeze([
  "Conservative",
  "Moderate",
  "Aggressive",
]);

/* ─── Analyst ratings ────────────────────────────────────────────── */

/**
 * URL of the arc-analyst-proxy Cloudflare Worker. When this env var is
 * set, the proxy is the primary source of truth and Firestore/seed
 * become disaster-recovery fallbacks. When unset, behavior is unchanged
 * (Firestore → seed) — which is what we ship Phase 6 with.
 *
 * See infra/workers/arc-analyst-proxy/ for the worker implementation
 * and deploy steps.
 */
const ANALYST_PROXY_URL = process.env.REACT_APP_RESEARCH_API_URL || null;

async function fetchAnalystRatingsFromProxy(filters) {
  const url = new URL(`${ANALYST_PROXY_URL}/ratings`);
  if (filters.sector) url.searchParams.set("sector", filters.sector);
  if (filters.riskCharacter)
    url.searchParams.set("riskCharacter", filters.riskCharacter);
  if (filters.sortBy) url.searchParams.set("sortBy", filters.sortBy);
  if (filters.sortDir) url.searchParams.set("sortDir", filters.sortDir);
  if (filters.pageSize)
    url.searchParams.set("pageSize", String(filters.pageSize));

  const res = await fetch(url.toString(), { credentials: "omit" });
  if (!res.ok) throw new Error(`proxy ${res.status}`);
  const data = await res.json();
  return Array.isArray(data.items) ? data.items : [];
}

/**
 * Fetch analyst ratings, optionally filtered.
 *
 * Resolution order:
 *   1. Proxy (REACT_APP_RESEARCH_API_URL) — preferred once set
 *   2. Firestore analystRatings collection
 *   3. In-memory seed data (analystSeedData.js)
 *
 * Once the licensed vendor integration goes live, the proxy returns
 * real data and the lower-tier fallbacks stop firing.
 *
 * Filters (all optional):
 *   sector            — string sector id
 *   riskCharacter     — "Conservative" | "Moderate" | "Aggressive"
 *   sortBy            — "asOfDate" | "successRate" | "avgReturn"
 *   sortDir           — "asc" | "desc"  (default desc)
 *   pageSize          — number  (default 50)
 */
export async function fetchAnalystRatings(filters = {}) {
  // 1. Try the proxy if configured.
  if (ANALYST_PROXY_URL) {
    try {
      const items = await fetchAnalystRatingsFromProxy(filters);
      if (items.length > 0) return items;
      // Empty response → fall through to lower tiers.
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        "[researchService] proxy fetch failed, falling back:",
        err.message
      );
    }
  }

  // 2. Try Firestore.
  try {
    const col = collection(db, "analystRatings");
    const clauses = [];

    if (filters.sector) {
      clauses.push(where("sector", "==", filters.sector));
    }
    if (filters.riskCharacter) {
      clauses.push(where("riskCharacter", "==", filters.riskCharacter));
    }

    // The sortBy field name in Firestore must match exactly. We support
    // top-level fields only; "analyst.successRate" is dotted.
    const sortBy = filters.sortBy || "asOfDate";
    const sortDir = filters.sortDir || "desc";
    clauses.push(orderBy(sortBy, sortDir));
    clauses.push(limit(filters.pageSize || 100));

    const q = query(col, ...clauses);
    const snap = await getDocs(q);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
    // Empty — fall through to mock.
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      "[researchService] analyst ratings fetch failed, falling back to seed:",
      err.message
    );
    // Fall through to mock.
  }

  // Fallback: in-memory seed data. Apply the same filters client-side.
  const { ANALYST_SEED_DATA } = await import(
    "../components/research/analystSeedData"
  );
  const sortBy = filters.sortBy || "asOfDate";
  const sortDir = filters.sortDir || "desc";

  const filtered = ANALYST_SEED_DATA.filter((r) => {
    if (filters.sector && r.sector !== filters.sector) return false;
    if (filters.riskCharacter && r.riskCharacter !== filters.riskCharacter) return false;
    return true;
  });

  // Read dotted paths (e.g., "analyst.successRate")
  const valueOf = (obj, path) =>
    path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);

  filtered.sort((a, b) => {
    const av = valueOf(a, sortBy);
    const bv = valueOf(b, sortBy);
    if (av === bv) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = av > bv ? 1 : -1;
    return sortDir === "desc" ? -cmp : cmp;
  });

  return filtered.slice(0, filters.pageSize || 100);
}

/* ─── User: disclosure acknowledgment ────────────────────────────── */

const DISCLOSURE_VERSION = "v1";

/**
 * Read the user's Research disclosure acknowledgment record.
 * Returns null if not acknowledged or version is stale.
 */
export async function fetchResearchDisclosureAck(uid) {
  if (!uid) return null;
  try {
    const snap = await getDoc(doc(db, "users", uid, "settings", "researchDisclosureAck"));
    if (!snap.exists()) return null;
    const data = snap.data();
    if (data.version !== DISCLOSURE_VERSION) return null; // stale
    return data;
  } catch (_) {
    return null;
  }
}

/**
 * Write the user's Research disclosure acknowledgment. Idempotent.
 */
export async function writeResearchDisclosureAck(uid, sections) {
  if (!uid) throw new Error("uid required");
  const record = {
    acknowledgedAt: serverTimestamp(),
    version: DISCLOSURE_VERSION,
    sections,
  };
  await setDoc(
    doc(db, "users", uid, "settings", "researchDisclosureAck"),
    record,
    { merge: true }
  );
  return record;
}

/* ─── User: research watchlist ───────────────────────────────────── */

/**
 * Watchlist storage shape (single doc, atomic reads/writes):
 *
 *   users/{uid}/settings/researchWatchlist
 *     {
 *       items: [
 *         { ticker: "AAPL", addedAt: "2026-06-20T17:00:00Z" },
 *         { ticker: "NVDA", addedAt: "2026-06-20T17:05:00Z" },
 *         ...
 *       ],
 *       updatedAt: serverTimestamp
 *     }
 *
 * Backward compat: older docs may have a flat `tickers: [...]` array
 * instead of `items: [...]`. fetchWatchlist + getWatchlistItems both
 * handle either shape and normalize on read.
 *
 * Why one doc instead of a subcollection: watchlists are small
 * (typically <50 tickers per user) and we always read the full list
 * to render the sidebar. One doc = one read, atomic writes, no
 * extra Firestore index needed.
 */

const WATCHLIST_DOC = (uid) =>
  doc(db, "users", uid, "settings", "researchWatchlist");

/**
 * Legacy helper — returns just the ticker strings. Preserved so
 * existing callers don't break. New code should prefer
 * getWatchlistItems() which returns the full {ticker, addedAt} shape.
 */
export async function fetchWatchlist(uid) {
  const items = await getWatchlistItems(uid);
  return items.map((it) => it.ticker);
}

export async function writeWatchlist(uid, tickers) {
  if (!uid) throw new Error("uid required");
  const items = tickers.map((t) => ({
    ticker: String(t).toUpperCase(),
    addedAt: new Date().toISOString(),
  }));
  await setDoc(
    WATCHLIST_DOC(uid),
    { items, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/**
 * Read the user's watchlist as an array of {ticker, addedAt} objects,
 * sorted newest-first. Handles both the new `items` shape and the
 * legacy `tickers` shape transparently.
 *
 * Returns [] for unauthenticated users, empty docs, and read errors.
 */
export async function getWatchlistItems(uid) {
  if (!uid) return [];
  try {
    const snap = await getDoc(WATCHLIST_DOC(uid));
    if (!snap.exists()) return [];
    const data = snap.data();

    // New shape: items array
    if (Array.isArray(data.items)) {
      return data.items
        .filter((it) => it && it.ticker)
        .map((it) => ({
          ticker: String(it.ticker).toUpperCase(),
          addedAt: it.addedAt || null,
        }))
        .sort((a, b) => (a.addedAt < b.addedAt ? 1 : -1));
    }

    // Legacy shape: flat tickers array. Synthesize empty addedAt.
    if (Array.isArray(data.tickers)) {
      return data.tickers
        .filter(Boolean)
        .map((t) => ({ ticker: String(t).toUpperCase(), addedAt: null }));
    }

    return [];
  } catch (_) {
    return [];
  }
}

/**
 * Add a ticker to the watchlist. Idempotent — adding an existing
 * ticker is a no-op, the original addedAt timestamp is preserved.
 *
 * Returns the updated items list (sorted newest-first) so callers
 * can update local state without a re-fetch.
 */
export async function addToWatchlist(uid, ticker) {
  if (!uid) throw new Error("uid required");
  const normalized = String(ticker || "").toUpperCase().trim();
  if (!normalized) throw new Error("ticker required");

  const current = await getWatchlistItems(uid);
  if (current.some((it) => it.ticker === normalized)) {
    return current; // already there — preserve original addedAt
  }
  const next = [
    { ticker: normalized, addedAt: new Date().toISOString() },
    ...current,
  ];
  await setDoc(
    WATCHLIST_DOC(uid),
    { items: next, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return next;
}

/**
 * Remove a ticker from the watchlist. Idempotent — removing a ticker
 * that isn't in the list is a no-op.
 *
 * Returns the updated items list.
 */
export async function removeFromWatchlist(uid, ticker) {
  if (!uid) throw new Error("uid required");
  const normalized = String(ticker || "").toUpperCase().trim();
  if (!normalized) throw new Error("ticker required");

  const current = await getWatchlistItems(uid);
  const next = current.filter((it) => it.ticker !== normalized);
  if (next.length === current.length) return current; // wasn't there

  await setDoc(
    WATCHLIST_DOC(uid),
    { items: next, updatedAt: serverTimestamp() },
    { merge: true }
  );
  return next;
}

/**
 * Quick "is this ticker in my watchlist" check. Doesn't hit Firestore
 * if the caller already has the items array.
 */
export function isInWatchlist(items, ticker) {
  if (!Array.isArray(items) || !ticker) return false;
  const normalized = String(ticker).toUpperCase();
  return items.some((it) => it.ticker === normalized);
}

/* ─── Research engine v2: cached snapshot direct-read ────────────── */

/**
 * Direct Firestore read of a cached snapshot. Bypasses the Worker
 * entirely — Firestore security rules allow any authenticated user
 * to read the `snapshots/{TICKER}` collection (the Worker writes via
 * service-account auth which bypasses rules).
 *
 * Used by WatchlistGrid cards to render a preview without paying
 * the cost of opening the streaming SSE connection just to read the
 * cached fields. Faster + cheaper than streamSnapshot for the
 * read-only "show me a card preview" use case.
 *
 * Returns null if the ticker has never been researched (no Firestore
 * doc) or if the read fails.
 */
export async function getCachedSnapshotDoc(ticker) {
  if (!ticker) return null;
  const normalized = String(ticker).toUpperCase().trim();
  try {
    const snap = await getDoc(doc(db, "snapshots", normalized));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      `[researchService] cached snapshot read failed for ${normalized}:`,
      err.message
    );
    return null;
  }
}

/**
 * Parse a Claude-generated equity analysis (Markdown) and pull out
 * the key scorecard metrics that the watchlist card displays.
 *
 * The grounded prompt produces a fairly consistent structure, so
 * regex is reliable for our purposes. If the prompt shape ever
 * changes materially, this function is the choke point to update.
 *
 * Risk Character is DERIVED from the Risk Profile Score, not
 * extracted directly — the prompt outputs a Risk Profile Score
 * (1.0-5.0 where HIGHER = SAFER, per prompt grounding rules).
 * We map:
 *   score >= 4.0   → Conservative (manageable risk)
 *   3.0 <= score < 4.0  → Moderate
 *   score < 3.0    → Aggressive (structural risk)
 * Future Phase 4+ will replace this with a rules-layer
 * computation that also factors in market-side signals.
 *
 * Returns an object with the four parsed fields, each possibly null
 * if the regex didn't match. Callers should handle nulls gracefully.
 */
export function extractAIMetrics(aiText) {
  if (!aiText || typeof aiText !== "string") {
    return {
      compositeScore: null,
      convictionBand: null,
      finalStance: null,
      riskCharacter: null,
      riskScore: null,
    };
  }

  // Composite rating, e.g. "Composite Rating: 4.32 / 5.00" or
  // "Composite Average: 4.32" — flexible match.
  const scoreMatch =
    aiText.match(/Composite\s*(?:Rating|Average)[^\d]*?(\d+\.?\d*)\s*\/\s*5/i) ||
    aiText.match(/Composite\s*(?:Rating|Average)[^\d]*?(\d+\.\d+)/i);
  const compositeScore = scoreMatch ? parseFloat(scoreMatch[1]) : null;

  // Conviction band — one of: Very High / High / Moderate / Selective / Avoid
  const bandMatch =
    aiText.match(/(Very High|High|Moderate|Selective|Avoid)\s+Conviction/i) ||
    aiText.match(/Conviction\s*(?:Band)?:\s*\**\s*(Very High|High|Moderate|Selective|Avoid)/i);
  const convictionBand = bandMatch ? `${bandMatch[1]} Conviction` : null;

  // Final stance — one of the six options the prompt enumerates
  const stanceMatch =
    aiText.match(/Final\s+Stance[^a-zA-Z]+(Strong Accumulate|Accumulate|Watchlist|Hold[- ]Monitor|Avoid|Speculative Only)/i) ||
    aiText.match(/(Strong Accumulate|Accumulate|Watchlist|Hold[- ]Monitor|Speculative Only)/);
  const finalStance = stanceMatch ? stanceMatch[1].replace(/-/g, " ") : null;

  // Risk Profile Score (1.0-5.0, higher = safer per prompt rules)
  const riskMatch = aiText.match(
    /Risk\s*Profile\s*Score[^\d]*?(\d+\.?\d*)\s*\/\s*5/i
  );
  const riskScore = riskMatch ? parseFloat(riskMatch[1]) : null;

  // Derive Risk Character bucket from the score
  let riskCharacter = null;
  if (riskScore != null) {
    if (riskScore >= 4.0) riskCharacter = "Conservative";
    else if (riskScore >= 3.0) riskCharacter = "Moderate";
    else riskCharacter = "Aggressive";
  }

  return {
    compositeScore,
    convictionBand,
    finalStance,
    riskCharacter,
    riskScore,
  };
}

/* ─── Research engine v2: snapshot fetching (Worker + SSE) ────────── */

/**
 * URL of the arc-research-proxy Worker. Set this in your Render env
 * vars (and locally in .env.local) when wiring up the frontend:
 *
 *   REACT_APP_RESEARCH_WORKER_URL=https://arc-research-proxy.arc-account-1e8.workers.dev
 *
 * If unset, streamSnapshot() throws immediately — there's no fallback
 * because the Worker IS the data source for Research engine v2.
 */
const RESEARCH_WORKER_URL =
  process.env.REACT_APP_RESEARCH_WORKER_URL || null;

export function getResearchWorkerUrl() {
  return RESEARCH_WORKER_URL;
}

/**
 * Stream a snapshot from arc-research-proxy. Consumes the Worker's
 * Server-Sent Events stream and dispatches each event to the
 * appropriate callback. Caller can render progressively (append AI
 * text as chunks arrive) or wait for `onComplete` and render once.
 *
 * Why fetch+ReadableStream instead of EventSource:
 *   - EventSource forces GET only, no custom headers, and no body.
 *     If we ever add auth tokens or POST refresh, fetch is required.
 *   - fetch+ReadableStream lets us cancel mid-stream (abort signal)
 *     for cleanup on unmount, which EventSource can't do cleanly.
 *
 * @param {Object} opts
 * @param {string} opts.ticker         - Ticker symbol (case-insensitive)
 * @param {boolean} [opts.forceRefresh] - If true, bypass Firestore cache
 *                                        and regenerate fresh
 * @param {AbortSignal} [opts.signal]  - Pass an AbortController.signal
 *                                        to allow caller to cancel
 *
 * @param {Object} callbacks - Per-event handlers, all optional
 * @param {Function} [callbacks.onSnapshot]   - (snapshot) — EDGAR data
 * @param {Function} [callbacks.onDataBlock]  - (block)    — what Claude saw
 * @param {Function} [callbacks.onAIStart]    - ({model})  — generation began
 * @param {Function} [callbacks.onChunk]      - ({text})   — text delta
 * @param {Function} [callbacks.onAIDone]     - (payload)  — full text + meta
 * @param {Function} [callbacks.onCached]     - ({cachedAt, source})
 * @param {Function} [callbacks.onNoAI]       - ({reason, entityType})
 *                                              ETF/fund/etc — no AI generated
 * @param {Function} [callbacks.onComplete]   - ()         — stream ended OK
 * @param {Function} [callbacks.onError]      - ({message, code})
 *
 * @returns {Promise<void>} resolves when the stream ends (success or
 *                          error). Errors are dispatched via onError —
 *                          the promise rejects only for hard failures
 *                          (network down, bad URL, missing env var).
 */
export async function streamSnapshot({ ticker, forceRefresh = false, signal }, callbacks = {}) {
  if (!RESEARCH_WORKER_URL) {
    const err = new Error(
      "REACT_APP_RESEARCH_WORKER_URL is not set. Add it to your env " +
        "vars before using the Research engine v2 features."
    );
    callbacks.onError?.({ message: err.message, code: "MISSING_CONFIG" });
    throw err;
  }
  if (!ticker) {
    const err = new Error("ticker required");
    callbacks.onError?.({ message: err.message, code: "BAD_INPUT" });
    throw err;
  }

  const url = new URL(
    `${RESEARCH_WORKER_URL}/snapshots/${String(ticker).toUpperCase()}`
  );
  url.searchParams.set("ai", "1");
  if (forceRefresh) url.searchParams.set("refresh", "1");

  const res = await fetch(url.toString(), {
    method: "GET",
    headers: { Accept: "text/event-stream" },
    signal,
  });

  if (!res.ok) {
    // Worker returns JSON error bodies for 4xx — try to parse and
    // surface the typed code (TICKER_NOT_FOUND, NO_REVENUE_DATA, etc.).
    let payload = null;
    try {
      payload = await res.json();
    } catch (_) {
      /* not JSON — fall through */
    }
    const message =
      payload?.detail || payload?.error || `Worker error ${res.status}`;
    const code = payload?.code || `HTTP_${res.status}`;
    callbacks.onError?.({ message, code, status: res.status });
    return;
  }

  if (!res.body) {
    callbacks.onError?.({
      message: "Worker returned no response body",
      code: "EMPTY_BODY",
    });
    return;
  }

  // ─── SSE parser ─────────────────────────────────────────────────
  //
  // Events from the Worker (see infra/workers/arc-research-proxy/worker.js
  // for the source of truth):
  //
  //   event: snapshot      data: { ...EDGAR fundamentals... }
  //   event: data_block    data: { ...DATA BLOCK sent to Claude... }
  //   event: ai_start      data: { model, usage }
  //   event: ai_chunk      data: { text }
  //   event: ai_done       data: { text, model, stopReason, usage, generatedAt }
  //   event: cached        data: { cachedAt, source }  (only on cache hits)
  //   event: complete      data: {}
  //   event: error         data: { message, code }
  //
  // SSE wire format: each event is "event: <name>\ndata: <json>\n\n".
  // We accumulate raw bytes into a buffer, split on "\n\n", parse
  // each complete event, and dispatch to the right callback.

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    let chunk;
    try {
      chunk = await reader.read();
    } catch (err) {
      if (signal?.aborted) return; // caller cancelled — clean exit
      callbacks.onError?.({
        message: err.message || String(err),
        code: "STREAM_ERROR",
      });
      return;
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });

    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);

      let eventType = "";
      let dataStr = "";
      for (const line of rawEvent.split("\n")) {
        if (line.startsWith("event:")) eventType = line.slice(6).trim();
        else if (line.startsWith("data:")) dataStr = line.slice(5).trim();
      }
      if (!dataStr) continue;

      let payload;
      try {
        payload = JSON.parse(dataStr);
      } catch (_) {
        continue; // malformed — skip
      }

      switch (eventType) {
        case "snapshot":   callbacks.onSnapshot?.(payload); break;
        case "data_block": callbacks.onDataBlock?.(payload); break;
        case "ai_start":   callbacks.onAIStart?.(payload); break;
        case "ai_chunk":   callbacks.onChunk?.(payload); break;
        case "ai_done":    callbacks.onAIDone?.(payload); break;
        case "cached":     callbacks.onCached?.(payload); break;
        case "no_ai":      callbacks.onNoAI?.(payload); break;
        case "complete":   callbacks.onComplete?.(); break;
        case "error":      callbacks.onError?.(payload); break;
        // unknown events are ignored
      }
    }
  }
}
